const REFUSAL = {
  NO_CALL_PERMISSION: 'This parent has not allowed calls.',
  DO_NOT_CALL: 'This parent asked not to be called.',
  AGENT_BUSY: 'You are already on a call.',
  PARENT_CALL_LIMIT: 'This parent has already had 100 calls in the last 24 hours.',
  CALLING_DISABLED: 'Calling is turned off.',
  REQUEST_CAP: 'You cannot ask again yet.',
  OPTED_OUT: 'This parent opted out, so a permission template cannot be sent.',
  TEMPLATE_NOT_READY: 'The call permission template is not approved yet.',
};

const TERMINAL = new Set(['rejected', 'no_answer', 'failed']);

export function isTerminalPhase(phase) {
  return TERMINAL.has(phase);
}

export function offerFromPermission(permission, now = Date.now()) {
  if (!permission || typeof permission !== 'object') return { mode: 'unavailable' };
  if (permission.do_not_call === true) return { mode: 'hidden' };
  const unanswered = Number(permission.consecutive_unanswered) || 0;
  if (permission.status === 'granted') {
    if (unanswered >= 2) return { mode: 'suggest' };
    return { mode: 'call' };
  }
  const nextAt = permission.next_request_at || null;
  const nextMs = nextAt ? new Date(nextAt).getTime() : NaN;
  if (Number.isFinite(nextMs) && nextMs > now) return { mode: 'wait', nextRequestAt: nextAt };
  if (permission.can_request === false) return { mode: 'wait', nextRequestAt: null };
  return { mode: 'ask' };
}

export function defaultAskReason({ agent, child } = {}) {
  const who = String(agent || '').trim() || 'we';
  const name = String(child || '').trim();
  if (name) return `Can ${who} call you about ${name}?`;
  return `Can ${who} call you?`;
}

export function dialRefusal(body) {
  const code = body?.error?.code || body?.code || '';
  const nextRequestAt = body?.next_request_at || body?.error?.next_request_at || null;
  const canRequest = body?.can_request ?? body?.error?.can_request;
  const message = REFUSAL[code] || body?.error?.message || body?.message || 'Call was not started';
  let patch = null;
  if (code === 'DO_NOT_CALL') patch = { do_not_call: true };
  if (code === 'NO_CALL_PERMISSION') {
    patch = {
      status: 'none',
      can_request: canRequest !== false,
      next_request_at: nextRequestAt,
      do_not_call: false,
    };
  }
  if (code === 'REQUEST_CAP') {
    patch = { can_request: false, next_request_at: nextRequestAt };
  }
  return { code, message, patch };
}

export function askResultCopy(body) {
  if (body?.mode === 'template') return 'The approved template was sent.';
  if (body?.mode === 'session') return 'Asked for permission in this chat.';
  return 'Asked for permission to call.';
}

export function nextOutboundCall(call, status) {
  if (!call) return call;
  if (status === 'parent_ringing') return { ...call, phase: 'ringing' };
  if (status === 'connected') return { ...call, phase: call.liveAt ? call.phase : 'connecting' };
  if (isTerminalPhase(status)) return { ...call, phase: status, liveAt: null };
  return call;
}

export function phaseFromEndReason(reason) {
  if (reason === 'rejected_by_parent' || reason === 'rejected') return 'rejected';
  if (reason === 'no_answer') return 'no_answer';
  if (reason === 'agent_media_failed' || reason === 'meta_media_failed' || reason === 'failed') return 'failed';
  return null;
}

export function callAfterSignalEnded(call, endReason) {
  if (!call) return null;
  if (isTerminalPhase(call.phase)) return call;
  const mapped = phaseFromEndReason(endReason);
  if (mapped) return { ...call, phase: mapped, liveAt: null };
  return null;
}

export function callAfterGatewayEnded(call) {
  if (!call) return null;
  if (isTerminalPhase(call.phase)) return call;
  if (call.outbound && !call.liveAt) return call;
  return null;
}

export function callStatusLabel(call, now = Date.now()) {
  if (!call) return '';
  if (call.phase === 'rejected') return 'They declined the call';
  if (call.phase === 'no_answer') return 'No answer';
  if (call.phase === 'failed') return 'The call failed';
  if (call.liveAt) return `${Math.floor((now - call.liveAt) / 1000)}s`;
  if (call.phase === 'ringing') return 'Ringing…';
  if (call.phase === 'calling') return 'Calling…';
  return 'Connecting…';
}
